<template>
  <div>
    <v-card-title>
      <v-row align="center">
        <v-col>
          <div class="subtitle-2 grey--text text--grey-100">
            {{ $t("card_title.participant_comparison") }}
          </div>
        </v-col>
        <v-col cols="auto">
          <v-menu
            v-model="menuVisible"
            offset-y
            @keydown.esc="menuVisible = false"
          >
            <template v-slot:activator="{ on }">
              <div>
                <v-btn
                  rounded
                  text
                  outlined
                  class="text-none"
                  v-on="on"
                >
                  <v-icon
                    x-small
                    class="mr-2"
                  >
                    far fa-pen
                  </v-icon>
                  {{ $t('button.edit_columns') }}
                </v-btn>
              </div>
            </template>

            <v-card width="350">
              <v-text-field
                v-model="search"
                dense
                outlined
                hide-details
                :label="$t('text_input.search.placeholder')"
                class="pa-2"
                clearable
                @click.stop
              />
              <div style="overflow: auto; flex-grow: 1; height: 300px">
                <draggable
                  :list="dirtySelectedVariables"
                  handle=".handle"
                  :group="{ name: 'variables' }"
                  :invert-swap="true"
                  :swap-treshold="0.6"
                  :animation="100"
                >
                  <div
                    v-for="(variable, index) in dirtySelectedVariables"
                    :key="'selected' + variable.id + index"
                  >
                    <div>
                      <v-list-item
                        @click.stop="removeVariable(index)"
                      >
                        <v-list-item-action>
                          <v-icon color="primary">
                            far fa-square-check
                          </v-icon>
                        </v-list-item-action>
                        <v-list-item-content class="pa-0">
                          <v-list-item-title>{{ variable.var_name }}</v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action>
                          <v-icon
                            small
                            class="handle"
                            style="cursor: move"
                          >
                            far fa-grip-lines
                          </v-icon>
                        </v-list-item-action>
                      </v-list-item>
                    </div>
                  </div>
                </draggable>
                <v-divider v-if="filteredNotSelectedVariables.length" />
                <v-list>
                  <v-list-item
                    v-for="variable in filteredNotSelectedVariables"
                    :key="variable.id"
                    @click.stop="dirtySelectedVariables.push(variable)"
                  >
                    <v-list-item-action>
                      <v-icon>
                        far fa-square
                      </v-icon>
                    </v-list-item-action>
                    <v-list-item-content class="pa-0">
                      <v-list-item-title>{{ variable.var_name }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </div>
            </v-card>
          </v-menu>
        </v-col>
      </v-row>
    </v-card-title>
    <v-card-text class="px-0">
      <v-data-table
        :items="diagnosticStats"
        :loading="loading"
        :headers="tableHeaders"
        :custom-sort="customSort"
        sort-by="last_name"
        :sort-desc="false"
        hide-default-footer
        disable-pagination
      >
        <template v-slot:item="{ item }">
          <tr
            class="clickable"
            @click="$emit('select:player', item.player)"
          >
            <td
              style="width: 0px"
              class="pr-0"
            >
              <v-avatar
                size="32px"
                class="mr-2"
                color="grey lighten-2"
              >
                <v-img :src="item.player.photo_url">
                  <template v-slot:placeholder>
                    <v-icon x-small>
                      fal fa-user
                    </v-icon>
                  </template>
                </v-img>
              </v-avatar>
            </td>
            <td>
              {{ item.player.last_name }} {{ item.player.first_name }}
            </td>
            <td
              v-for="tableHeader in tableHeaders.slice(2)"
              :key="tableHeader.text"
              class="font-weight-bold"
            >
              <table-bar-value
                v-if="getValue(item.variables, tableHeader.value, 'norm_z_score')"
                :value="getValue(item.variables, tableHeader.value, 'norm_z_score')"
                :color="getColorFromValue(getValue(item.variables, tableHeader.value, 'norm_z_score'), 100)"
              >
                {{ getValue(item.variables, tableHeader.value, viewType) }}{{ getUnit(item.variables, tableHeader.value) }}
              </table-bar-value>
              <span v-else>
                {{ getValue(item.variables, tableHeader.value, viewType) ? getValue(item.variables, tableHeader.value, viewType) : 'N/A' }}{{ getUnit(item.variables, tableHeader.value) }}
              </span>
            </td>
          </tr>
        </template>
        <template v-slot:body.append>
          <tr>
            <td
              class="font-weight-bold"
              style="border-top: 1px solid black;"
              colspan="2"
            >
              {{ $t("diagnostics.averages.label") }}
            </td>
            <td
              v-for="tableHeader in tableHeaders.slice(2)"
              :key="tableHeader.text"
              class="font-weight-bold"
              style="border-top: 1px solid black;"
            >
              {{ getAverageValue(tableHeader.value) }}{{ diagnosticStats.length > 0 ? getUnit(diagnosticStats[0].variables, tableHeader.value) : '' }}
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card-text>
  </div>
</template>

<script>
import { getColorForValue } from '@/utils/helpers'
import TableBarValue from '@/components/helpers/TableBarValue.vue'
import draggable from 'vuedraggable'

export default {
  name: 'DiagnosticResultsTable',
  components: {
    TableBarValue,
    draggable
  },
  props: {
    diagnosticStats: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    availableVariables: {
      type: Array,
      required: true
    },
    selectedVariables: {
      type: Array,
      required: true
    },
    viewType: {
      type: String,
      default: 'value'
    }
  },
  data: () => ({
    colors: [
      '#DC143Ccc',
      '#FF6347cc',
      '#FFA500cc',
      '#FFD700cc',
      '#87CEEBcc',
      '#ADDEFFcc',
      '#90EE90cc',
      '#32CD32cc',
      '#228B22cc',
      '#006400cc'
    ],
    menuVisible: false,
    search: ''
  }),
  computed: {
    tableHeaders() {
      const headers = [
        { sortable: false, class: 'pr-0' },
        { text: this.$t('table_headers.name'), value: 'last_name', width: '150px' }
      ]

      for (const variable of this.dirtySelectedVariables) {
        headers.push({ text: variable.var_name, value: variable.var_id, unit: variable.unit })
      }

      return headers
    },
    filteredNotSelectedVariables() {
      if (this.search === null) return this.availableVariables.filter(v => !this.dirtySelectedVariables.includes(v))

      return this.availableVariables.filter(v => !this.dirtySelectedVariables.includes(v) && v.var_name.toLowerCase().includes(this.search.toLowerCase()))
    },
    dirtySelectedVariables: {
      get() {
        return this.selectedVariables
      },
      set(value) {
        this.$emit('update:selectedVariables', value)
      }
    }
  },
  watch: {
  },
  methods: {
    getColorFromValue(value, max) {
      return getColorForValue(value, max, false, this.colors)
    },
    getValue(playerVariables, varId, view) {
      const variable = playerVariables.find(v => v.var_id === varId)
      return variable ? variable[view] : null
    },
    getUnit(playerVariables, varId) {
      if (this.viewType === 'norm_value' || this.viewType === 'norm_z_score') return '%'
      if (this.viewType === 'z_score') return null

      const variable = playerVariables.find(v => v.var_id === varId)
      return variable ? variable.unit : null
    },
    getAverageValue(varId) {
      const values = this.diagnosticStats.map(player => {
        const variable = player.variables.find(v => v.var_id === varId)
        return variable ? variable[this.viewType] : null
      }).filter(value => value !== null && !isNaN(value))

      if (values.length === 0) return 0

      const sum = values.reduce((acc, val) => acc + Number(val), 0)
      const average = sum / values.length
      return parseFloat(average.toFixed(2))
    },
    customSort(items, index, isDesc) {
      items.sort((a, b) => {
        if (index[0] === 'last_name') {
          if (!isDesc[0]) {
            return a.player.last_name.localeCompare(b.player.last_name)
          } else {
            return b.player.last_name.localeCompare(a.player.last_name)
          }
        } else {
          const variable1 = a.variables.find(v => v.var_id === index[0])
          const variable2 = b.variables.find(v => v.var_id === index[0])

          const aValue = variable1 ? variable1.value : 0
          const bValue = variable2 ? variable2.value : 0

          if (!isDesc[0]) {
            return aValue - bValue
          } else {
            return bValue - aValue
          }
        }
      })
      return items
    },
    removeVariable(index) {
      this.dirtySelectedVariables.splice(index, 1)
    }
  }
}
</script>
